import { useCallback, useMemo, useState, ChangeEvent } from 'react';
import { Chip, IconButton, Box, Button, Dialog, DialogTitle, DialogContent, Typography, TablePagination } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { useLanguage } from '../../../../context/LanguageContext';
import { useOptimisticStore } from '../../../../context/OptimisticStoreContext';
import { Store, Contact } from '../../../../types/Store';
import StoreCard from './common/StoreCard';
import StoreTable from './common/StoreTable';
import { ContactModal, DeleteConfirmModal } from '../../UpdateStore/components/Drawers';
import useDeleteContact from '../../../../hooks/Magasins/useDeleteContact';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../../../../utils/phoneFormatter';

interface ContactMagProps {
    store: Store;
    isEditable?: boolean;
}

function ContactMag({ store, isEditable = false }: ContactMagProps) {
    const { t } = useLanguage();
    const { deleteContact } = useDeleteContact();
    const { displayStore, actualStore, setActualStore } = useOptimisticStore();
    const contacts = (displayStore?.CONTACT_ENTITE || store.CONTACT_ENTITE) || [];

    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [contactInfo, setContactInfo] = useState<Contact | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const getTrapLevelLabel = useCallback((trapLevel?: string) => {
        switch (trapLevel?.toString()) {
            case '1':
                return t('pages.store.trapAddress.client');
            case '2':
                return t('pages.store.trapAddress.kissTheBride');
            default:
                return t('pages.store.trapAddress.none');
        }
    }, [t]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedContacts = useMemo(() => {
        const start = page * rowsPerPage;
        return contacts.slice(start, start + rowsPerPage);
    }, [contacts, page, rowsPerPage]);

    const handleInfo = (contact: Contact) => {
        setContactInfo(contact);
        setInfoDialogOpen(true);
    };

    const handleEdit = (contact: Contact) => {
        setSelectedContact(contact);
        setContactModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedContact(null);
        setContactModalOpen(true);
    };

    const handleDeleteClick = (contact: Contact) => {
        setContactToDelete(contact);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!contactToDelete) return;

        setDeleteModalOpen(false);
        const contactIdToDelete = contactToDelete.CONTACT_ID;
        const previousStore = actualStore;
        setContactToDelete(null);

        if (actualStore) {
            const updatedContacts = (actualStore.CONTACT_ENTITE || []).filter((c: Contact) => c.CONTACT_ID !== contactIdToDelete);
            setActualStore({ ...actualStore, CONTACT_ENTITE: updatedContacts });
        }

        const success = await deleteContact(contactIdToDelete);
        
        if (success) {
            toast.success(t('pages.store.success.contactDeleted'));
        } else {
            if (previousStore) setActualStore(previousStore);
            toast.error(t('pages.store.errors.contactDeleteFailed'));
        }
    };

    const columns = useMemo(() => {
        const baseColumns = [
            { label: t('pages.store.name'), key: 'LASTNAME' },
            { label: t('pages.store.firstName'), key: 'FIRSTNAME' },
            { label: t('pages.store.job'), key: 'JOB' },
            {
                label: t('pages.store.trapLevel'),
                align: 'center' as const,
                render: (contact: Contact) => (
                    <Chip
                        label={getTrapLevelLabel(contact.TRAP_LEVEL)}
                        size="small"
                        color={['1', '2'].includes(contact.TRAP_LEVEL?.toString() || '') ? 'warning' : 'default'}
                        variant={['1', '2'].includes(contact.TRAP_LEVEL?.toString() || '') ? 'filled' : 'outlined'}
                    />
                ),
            },
            {
                label: t('common.info'),
                align: 'center' as const,
                render: (contact: Contact) => (
                    <IconButton size="small" onClick={() => handleInfo(contact)} color="info">
                        <InfoIcon fontSize="small" />
                    </IconButton>
                ),
            },
        ];

        if (isEditable) {
            baseColumns.push({
                label: t('common.actions'),
                align: 'center' as const,
                render: (contact: Contact) => (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => handleEdit(contact)} color="primary">
                            <ModeEditOutlineIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(contact)} color="error">
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ),
            });
        }

        return baseColumns;
    }, [t, isEditable, getTrapLevelLabel]);

    return (
        <>
            <StoreCard
                title={t('pages.store.contactMag')}
                icon={<PersonIcon sx={{ color: '#556ee6' }} />}
            >
                {contacts.length > 0 && (
                    <>
                        <StoreTable columns={columns} data={paginatedContacts} />
                        <TablePagination
                            component="div"
                            count={contacts.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage={t('common.rowsPerPage')}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of')} ${count}`}
                        />
                    </>
                )}
                {contacts.length===0 && <div style={{ textAlign: 'center', padding: '16px' }}>{t('pages.store.noContacts')}</div>}
                {isEditable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button 
                            variant="text" 
                            size="small"
                            startIcon={<AddIcon />} 
                            onClick={handleAdd}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('pages.store.addContact')}
                        </Button>
                    </Box>
                )}
            </StoreCard>

            <ContactModal
                open={contactModalOpen}
                onClose={() => {
                    setContactModalOpen(false);
                    setSelectedContact(null);
                }}
                entityId={store.ENTITY_ID}
                contact={selectedContact}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setContactToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title={t('pages.store.deleteContact')}
                message={t('pages.store.confirmDeleteContact')}
            />

            <Dialog 
                open={infoDialogOpen} 
                onClose={() => {
                    setInfoDialogOpen(false);
                    setContactInfo(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('pages.store.contactInfo')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body1">
                            <strong>{t('pages.store.name')} :</strong> {contactInfo?.LASTNAME} {contactInfo?.FIRSTNAME}
                        </Typography>
                        <Typography variant="body1">
                            <strong>{t('pages.store.phone')} :</strong> {contactInfo?.COMM_PHONE_DIALING_CODE ? `${contactInfo.COMM_PHONE_DIALING_CODE} ` : ''}{formatPhoneNumber(contactInfo?.PHONE_NUMBER) || '-'}
                        </Typography>
                        <Typography variant="body1">
                            <strong>{t('pages.store.mobile')} :</strong> {contactInfo?.COMM_CELL_PHONE_DIALING_CODE ? `${contactInfo.COMM_CELL_PHONE_DIALING_CODE} ` : ''}{formatPhoneNumber(contactInfo?.CELL_PHONE_NUMBER) || '-'}
                        </Typography>
                        <Typography variant="body1">
                            <strong>{t('pages.store.email')} :</strong> {contactInfo?.EMAIL_ADDRESS || '-'}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ContactMag;
